import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Tabs } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendar, faList } from '@fortawesome/pro-solid-svg-icons'
import { useSelector } from 'react-redux'

import InternshipsManagerView from '../../shared/InternshipsManagerView'
import InternshipRequestsView from './InternshipRequestsView/InternshipRequestsView'
import PlacesOccupancyView from './PlacesOccupancy/PlacesOccupancyView'
import InternshipsListView from './InternshipsList/InternshipsListView'
import IndividualInternshipRequestModal from '../../../Providers/Forms/IndividualInternshipRequest/IndividualInternshipRequestModal'
import { InstitutionContext } from '../../../Providers/InstitutionProvider'
import InternshipRequestFilesDrawer from './InternshipRequestsView/InternshipRequestFilesDrawer'
import useSessionStorage from '../../../hooks/UseSessionStorage'
import { selectUser } from '../../../reducers/UserReducer'
import { isHospitalAdmin, isInstitutionGroupAdmin } from '../../../utils/roles'
import DefaultContainer from '../../../HOC/DefaultContainer'
import useBetaMode from '../../../hooks/useBetaMode'
import ExperimentalTitle from '../../../Components/shared/ExperimentalTitle'
import { getAllSchools } from '../../../utils/api/school'
import { SerializationContexts } from '../../../utils/api/internship'
import useRequest from '../../../hooks/useRequest'
import SelectProvider from '../../../Providers/SelectProvider'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'

const { TabPane } = Tabs

export const INTERNSHIP_VIEW_ACTIVE_TAB_KEY = 'institution.internships_view_active_key'

/** @enum {string} */
export const InternshipsViewTabs = Object.freeze({
  LIST: '1',
  CALENDAR: '2',
  PLACES_OCCUPANCY: '3',
  REQUESTS: '4'
})

const InternshipsView = () => {
  const institutionContext = useContext(InstitutionContext)

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [activeTab, setActiveTab] = useState(InternshipsViewTabs.LIST)

  const [storedValue, updateStoredValue] = useSessionStorage(INTERNSHIP_VIEW_ACTIVE_TAB_KEY + user.id, activeTab)
  const betaMode = useBetaMode()

  const schools = useRequest({
    request: getAllSchools,
    parameters: useMemo(() => ({ contexts: [SerializationContexts.SCHOOL], security: { areas: false } }), [])
  })

  const handleTabChange = useCallback((value) => {
    updateStoredValue(value)
    setActiveTab(value)
  }, [updateStoredValue, setActiveTab])

  /** @note This effect is needed to keep in sync with the session storage because we cannot listen to it directly. */
  useEffect(() => setActiveTab(String(storedValue)), [storedValue, setActiveTab])

  const canViewAdminTabs = useMemo(() => isHospitalAdmin(user), [user])
  const canViewInternshipRequests = useMemo(() => canViewAdminTabs || isInstitutionGroupAdmin(user), [canViewAdminTabs, user])
  const requestInstitutions = useMemo(
    () => institutionContext && institutionContext.institution ? [institutionContext.institution] : [],
    [institutionContext]
  )

  return (
    <Tabs activeKey={activeTab} animated={false} onChange={handleTabChange}>
      <TabPane tab={<div><FontAwesomeIcon icon={faList} /> <span>{t('List view')}</span></div>} key={InternshipsViewTabs.LIST}>
        {activeTab === InternshipsViewTabs.LIST && (
          <InternshipsListView />
        )}
      </TabPane>
      <TabPane tab={<div><FontAwesomeIcon icon={faCalendar} /> <span>{t('internships_view.titles.internships_timeline')}</span></div>} key={InternshipsViewTabs.CALENDAR}>
        <SelectProvider>
          <InternshipsManagerView />
        </SelectProvider>
      </TabPane>
      {canViewAdminTabs && betaMode.active && (
        <TabPane tab={<ExperimentalTitle title='internships_view.titles.places_occupancy' />} key={InternshipsViewTabs.PLACES_OCCUPANCY}>
          <DefaultContainer>
            <PlacesOccupancyView />
          </DefaultContainer>
        </TabPane>
      )}
      {canViewInternshipRequests && (
        <TabPane tab={<div><FontAwesomeIcon icon={faList} /> <span>{t('internships_view.titles.internships_request')}</span></div>} key={InternshipsViewTabs.REQUESTS}>
          <IndividualInternshipRequestModal institutions={requestInstitutions} schools={schools} sectors={institutionContext.sectors}>
            <InternshipRequestFilesDrawer>
              <InternshipRequestsView />
            </InternshipRequestFilesDrawer>
          </IndividualInternshipRequestModal>
        </TabPane>
      )}
    </Tabs>
  )
}

export default InternshipsView
